import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Switch,
    TextField,
    Button,
    Typography,
    Divider
} from '@mui/material';
import { blockIP, unblockIP, getBlockedIPs } from '../../services/clicks';
import Loading from './Loading';

function IPBlacklist({ user }) {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null); 
    const [blockedIPs, setBlockedIPs] = useState([]);

    const [page, setPage] = useState(0); // Pagination: current page
    const [rowsPerPage, setRowsPerPage] = useState(10); // Pagination: rows per page

    const [ipToSearch, setIpToSearch] = useState('');
    const [ipToBlock, setIpToBlock] = useState('');

    const loadIPs = async () => {
        setError(null);
        setIsLoading(true);
        try {
            const { blockedIPs } = await getBlockedIPs(user._id)
            setBlockedIPs(blockedIPs);
        } catch (error) {
            setError('Error fetching IPs. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadIPs();
    }, []);

    // Handle Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchIP = async () => {
        setError(null);
        setIsLoading(true);
        setIpToBlock(null);
        console.log("ipToSearch: ", ipToSearch);
        try {
            const init = atob('aXBiX2xpdmVf');
            const access = atob('Ynh2MG5KR3dvc0RUekdUczMxQmJxNGlXNHJhOTRX');
            const token = atob('OGZMZmplQUJMRA==');
            const url = `https://api.ipbase.com/v2/info?apikey=${init}${access}${token}&ip=${ipToSearch}`;
            
            const response = await fetch(url, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const { data } = await response.json();
            setIpToBlock(data)
        } catch (error) {
            setError('Error fetching IPs. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Box>
            {/* Input Section for Blocking IP */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    margin: 2,
                    padding: 2, 
                    borderRadius: 1 
                }}
            >
                <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 2 }}>
                        {t('ipBlacklist.enterIPToBlock')}
                    </Typography>
                    <TextField
                        value={ipToSearch}
                        onChange={(e) => setIpToSearch(e.target.value)}
                        placeholder={t('ipBlacklist.enterIPPlaceholder')}
                        variant="outlined"
                        size="small"
                        sx={{ 
                            marginRight: 2, 
                            width: { xs: '40%', sm: '300px' }, 
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearchIP}
                        disabled={!ipToSearch}
                    >
                        {t('ipBlacklist.find')}
                    </Button>
                </Box>
            </Box>

            {/* Found IP on Search Field for larger screens */}
            { ipToBlock && 
            <TableContainer
                sx={{
                display: { xs: 'none', md: 'flex' },
                padding: 2
            }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.ip')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.region')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.toggleBlock')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow key={ipToBlock?.ip}>
                        <TableCell>{ipToBlock?.location?.country?.emoji || ""}{ipToBlock?.ip || "N/A"}</TableCell>
                        <TableCell>{ipToBlock?.location?.region?.name || "N/A"}</TableCell>
                        <TableCell>
                        <Switch
                            checked={blockedIPs.some(ip => ip.info?.ip === ipToBlock?.ip)}
                            onChange={async () => { 
                                await blockIP(ipToBlock?.ip, user._id) 
                                setIpToBlock(null);
                                loadIPs();
                            }}
                            inputProps={{ 'aria-label': 'controlled' }}
                            />
                        </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            } 
            
            {ipToBlock && (
                <Box
                sx={{
                    display: { xs: 'block', md: 'none' },
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: '#fff',
                    boxShadow: 1,
                    margin: 2,
                    width: '90%',
                }}
                >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    IP: <span>{ipToBlock.ip || 'N/A'}</span>
                </Typography>

                <Typography variant="body2">
                    <strong>{`${t('ipBlacklist.country')}:`}</strong> {ipToBlock?.location?.country?.emoji || 'N/A'}
                </Typography>

                <Typography variant="body2">
                    <strong>{`${t('ipBlacklist.region')}:`}</strong> {ipToBlock?.location?.region?.name || 'N/A'}
                </Typography>

                <Typography variant="body2">
                    <strong>{`${t('ipBlacklist.status')}:`}</strong>{' '}
                    {blockedIPs.some(ip => ip.info?.ip === ipToBlock?.ip) ? t('ipBlacklist.blocked') : t('ipBlacklist.allowed')}
                </Typography>

                <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end', 
                    mt: 2 
                }}>
                    <Typography>{t('ipBlacklist.block')}</Typography>
                    <Switch
                    checked={blockedIPs.some(ip => ip.info?.ip === ipToBlock?.ip)}
                    onChange={async () => {
                        await blockIP(ipToBlock?.ip, user._id);
                        setIpToBlock(null);
                        loadIPs();
                    }}
                    inputProps={{ 'aria-label': t('ipBlacklist.toggleBlock') }}
                    />
                </Box>
                </Box>
            )}  

            <Typography 
                variant="subtitle1" 
                sx={{ margin: 4, textAlign: "center", fontWeight: 'bold' }}>
                {t('ipBlacklist.blockedIPList')}
            </Typography>

            {/* Table Section for larger screens */}
            <TableContainer
                sx={{
                padding: { xs: 0, md: 2 },
            }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5'}}>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.ip')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.region')}</TableCell>
                            <TableCell sx={{fontWeight: 'bold'}}>{t('ipBlacklist.toggleBlock')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {blockedIPs
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((entry) => {
                                const ipInfo = entry.info || {};
                                return (
                                    (
                                        <TableRow key={ipInfo?.ip}>
                                            <TableCell sx={{ color: 'red' }}>{ipInfo?.location?.country?.emoji + " " || ""}{ipInfo?.ip || "N/A"}</TableCell>
                                            <TableCell sx={{ color: 'red' }}>{ipInfo?.location?.region?.name || "N/A"}</TableCell>
                                            <TableCell>
                                            <Switch
                                                checked={true}
                                                onChange={async () => { 
                                                    await unblockIP(ipInfo?.ip, user._id) 
                                                    loadIPs();
                                                }}
                                                inputProps={{ 'aria-label': 'controlled' }}
                                            />
                                            </TableCell>
                                        </TableRow>
                                    )
                                )
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={blockedIPs.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    marginBottom: 4
                }}
            />
        
        <Loading 
            text={t('ipBlacklist.loading')} 
            isLoading={isLoading} 
        />
        </Box>
    );
}

export default IPBlacklist;